import React, { useMemo, useState } from 'react';
import { Card } from '@uhg-abyss/web/ui/Card';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { WlmTransactionDetailModal } from './WlmTransactionDetailModal';
import { useGetWorkLoadFileSummaryQuery } from '@src/store/services/wlm/onlineServices';
import { formatDate } from '@src/utils';
import { buildCsvFromRows, downloadCsv } from '@src/utils/tableUtils';
import {
  SummaryTable,
  sectionBarStyle,
  renderMultilineText,
} from './SummaryTable';

const infoBoxStyle: React.CSSProperties = {
  border: '1px solid #cbcccd',
  padding: '15px',
  marginBottom: '15px',
  minHeight: '20px',
};

const HOURS_OPTIONS = ['36', '18', '12', '6', '3'].map((value) => ({
  label: value,
  value,
}));

const FILE_SUMMARY_BUCKETS = [
  {
    key: 'filesEnqueuedDataLoading',
    title: 'FILES THAT ARE ENQUEUED FOR DATA LOADING',
  },
  { key: 'filesDataLoading', title: 'FILES THAT ARE DATA LOADING' },
  {
    key: 'filesStrandedDataLoading',
    title: 'FILES THAT APPEAR TO BE STRANDED DURING DATA LOADING',
  },
  {
    key: 'filesActivelyCodeProcessing',
    title: 'FILES THAT ARE ACTIVELY CORE PROCESSING',
  },
  {
    key: 'filesErrorsCodeProcessing',
    title: 'FILES THAT HIT ERRORS DURING CORE PROCESSING',
  },
  { key: 'filesErrors', title: 'FILES THAT HIT ERRORS' },
];

const TRANS_SUMMARY_BUCKETS = [
  { key: 'transSetDataLoadng', title: 'TRANSSETS THAT ARE DATA LOADING' },
  {
    key: 'transSetStrandedDataLoadng',
    title: 'TRANSSETS THAT APPEAR TO BE STRANDED DURING DATA LOADING',
  },
  {
    key: 'transSetFamilyProcessing',
    title: 'TRANSSETS THAT ARE FAMILY PROCESSING',
  },
  {
    key: 'transSetErroredFamilyProcessing',
    title: 'TRANSSETS THAT ERRORED DURING FAMILY PROCESSING',
  },
  {
    key: 'transSetPostFamilyProcessing',
    title: 'TRANSSETS THAT ARE POST FAMILY PROCESSING',
  },
  {
    key: 'transSetUpdatingCes',
    title: 'TRANSSETS THAT ARE UPDATING DESTINATION SYSTEMS',
  },
  { key: 'transSetErrored', title: 'TRANSSETS THAT ERRORED' },
];

const fileColumns = (onNavigateToTransSet?: (transSetId: string) => void) => [
  { Header: 'Submitter', accessor: 'Submitter', minWidth: 140 },
  { Header: 'File ID', accessor: 'FileId', minWidth: 100 },
  { Header: 'Received', accessor: 'Received', minWidth: 160 },
  { Header: 'BulkLoaded', accessor: 'BulkLoaded', minWidth: 120 },
  {
    Header: 'Status',
    accessor: 'Status',
    minWidth: 160,
    Cell: ({ value }: any) => renderMultilineText(value),
  },
  {
    Header: 'Trans Sets',
    accessor: 'TransSets',
    minWidth: 100,
    Cell: ({ value }: any) => {
      const transSetId = typeof value === 'string' ? value.trim() : value;
      if (!transSetId) return null;
      if (!onNavigateToTransSet) return transSetId;
      return (
        <Link
          href={`#transset-${transSetId}`}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onNavigateToTransSet(transSetId);
          }}
        >
          {transSetId}
        </Link>
      );
    },
  },
];

// Backend sends these as objects (e.g. { Inbound: 4, Source: 0 }); render each
// entry on its own line. Wrapped in one div since the cell itself is a flex
// row container, which would otherwise lay out sibling divs side by side.
const renderCounts = (value?: Record<string, number | string> | null) =>
  value ? (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {Object.entries(value).map(([key, val]) => (
        <div key={key}>{`${key}=${val}`}</div>
      ))}
    </div>
  ) : null;

const transColumns = (onDetail: any) => [
  {
    Header: 'Transaction Set ID',
    accessor: 'TransactionSetId',
    minWidth: 150,
    // Scroll target for the Active File Summary 'Trans Sets' links.
    Cell: ({ value }: any) =>
      value ? <div id={`transset-${value}`}>{value}</div> : null,
  },
  { Header: 'Submitter', accessor: 'Submitter', minWidth: 140 },
  { Header: 'File ID', accessor: 'FileId', minWidth: 100 },
  {
    Header: 'Status',
    accessor: 'Status',
    minWidth: 160,
    Cell: ({ value }: any) => renderMultilineText(value),
  },
  { Header: 'Case Structure', accessor: 'CaseStructure', minWidth: 140 },
  {
    Header: 'Transaction Counts',
    accessor: 'TransactionCounts',
    minWidth: 160,
    Cell: ({ value }: any) => renderCounts(value),
  },
  {
    Header: 'Work Unit Counts',
    accessor: 'WorkUnitCounts',
    minWidth: 160,
    Cell: ({ value }: any) => renderCounts(value),
  },
  {
    Header: 'Detail',
    accessor: '',
    minWidth: 100,
    Cell: ({ row }: any) => (
      <Button
        size="sm"
        onClick={() =>
          onDetail({
            transSetId: row?.original?.TransactionSetId,
            bucket: row?.original?.Bucket,
          })
        }
      >
        Detail
      </Button>
    ),
  },
];

const fileCompletedColumns = [
  { Header: 'File Submitter', accessor: 'FileSubmitter', minWidth: 160 },
  { Header: 'File ID', accessor: 'FileId', minWidth: 100 },
  { Header: 'Trans Sets', accessor: 'TransactionSetId', minWidth: 100 },
  { Header: 'Submitter', accessor: 'Submitter', minWidth: 140 },
  {
    Header: 'Status',
    accessor: 'Status',
    minWidth: 160,
    Cell: ({ value }: any) => renderMultilineText(value),
  },
];

// fileSummaryData/transactionSummaryData arrive as an array of single-key
// bucket objects (e.g. [{ filesStrandedDataLoading: [...] }]); flatten to a map.
const mergeBuckets = (buckets?: Array<Record<string, any>>) =>
  (buckets || []).reduce((acc, bucket) => ({ ...acc, ...bucket }), {});

// fileCompletedSummaryData nests its trans sets; emit one row per trans set.
const flattenFileCompleted = (rows?: Array<any>) =>
  (rows || []).flatMap((row) =>
    (row?.TransSets?.length ? row.TransSets : [{}]).map((transSet: any) => ({
      FileSubmitter: row?.FileSubmitter,
      FileId: row?.FileId,
      TransactionSetId: transSet?.TransactionSetId,
      Submitter: transSet?.Submitter,
      Status: row?.Status,
    }))
  );

// Only these Active File Summary buckets get a 'Trans Sets' hyperlink.
const LINKABLE_FILE_SUMMARY_BUCKETS = new Set([
  'filesEnqueuedDataLoading',
  'filesDataLoading',
  'filesStrandedDataLoading',
  'filesActivelyCodeProcessing',
  'filesErrorsCodeProcessing',
  'filesErrors',
]);

// Scrolls to the matching row in Active Transaction Set Summary instead of
// opening a modal.
const scrollToTransSet = (transSetId: string) => {
  document
    .getElementById(`transset-${transSetId}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const WorkLoadMonitor = () => {
  const [timeInterval, setTimeInterval] = useState('36');
  const [detailParams, setDetailParams] = useState<{
    transSetId: string | number;
    bucket: string | number;
  } | null>(null);

  const { data, isFetching, refetch } = useGetWorkLoadFileSummaryQuery({
    dataSource: 'CDB',
    timeInterval,
  });

  const tCols = useMemo(() => transColumns(setDetailParams), []);
  const fColsLinked = useMemo(() => fileColumns(scrollToTransSet), []);
  const fColsPlain = useMemo(() => fileColumns(), []);

  const fileSummary = useMemo(
    () => mergeBuckets(data?.fileSummaryData),
    [data]
  );
  const transSummary = useMemo(
    () => mergeBuckets(data?.transactionSummaryData),
    [data]
  );
  const fileCompletedRows = useMemo(
    () => flattenFileCompleted(data?.fileCompletedSummaryData),
    [data]
  );
  const info = data?.workLoadMonitorInfo;

  // One entry per table currently shown on the page, in on-screen order.
  const downloadSections = useMemo(
    () => [
      ...FILE_SUMMARY_BUCKETS.filter(({ key }) => fileSummary?.[key]?.length).map(
        ({ key, title }) => ({
          title,
          columns: LINKABLE_FILE_SUMMARY_BUCKETS.has(key)
            ? fColsLinked
            : fColsPlain,
          rows: fileSummary[key],
        })
      ),
      ...TRANS_SUMMARY_BUCKETS.filter(
        ({ key }) => transSummary?.[key]?.length
      ).map(({ key, title }) => ({
        title,
        columns: tCols,
        rows: transSummary[key],
      })),
      ...(fileCompletedRows.length
        ? [
            {
              title: 'File Completed Summary',
              columns: fileCompletedColumns,
              rows: fileCompletedRows,
            },
          ]
        : []),
    ],
    [fileSummary, transSummary, fileCompletedRows, fColsLinked, fColsPlain, tCols]
  );

  const handleDownloadAll = () =>
    downloadCsv(
      downloadSections
        .map(
          ({ title, columns, rows }) =>
            `"${title.replace(/"/g, '""')}"\r\n${buildCsvFromRows(rows, columns)}`
        )
        .join('\r\n\r\n'),
      `wlm-${timeInterval}h.csv`
    );
  const monitorInfoItems = info
    ? [
        `The number of Files processed were: ${info.numberOfFilesProcessed}`,
        `The number of Transaction Sets processed were: ${info.numberOfTransSetsProcessed}`,
        `The number of Files Completed were: ${info.numberOfFilesCompleted}`,
        `Generated Time: ${formatDate(info.generatedTime)}`,
      ]
    : undefined;

  return (
    <Card css={{ 'abyss-card-root': { padding: '0' } }}>
      <div style={{ padding: '16px 24px' }}>
        <Flex
          css={{
            gap: '15px',
            alignItems: 'center',
            flexWrap: 'wrap',
            paddingBottom: '15px',
          }}
        >
          <Text css={{ fontWeight: 'bold', color: '#212223', fontSize: '14px' }}>
            Select Hours
          </Text>
          <SelectInput
            label="Select Hours"
            hideLabel
            value={timeInterval}
            onChange={setTimeInterval}
            options={HOURS_OPTIONS}
            css={{ 'abyss-select-input-root': { width: '120px' } }}
          />
          <Button size="sm" disabled={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button
            size="sm"
            disabled={!downloadSections.length}
            onClick={handleDownloadAll}
            css={{ marginLeft: 'auto' }}
          >
            Download CSV
          </Button>
        </Flex>

        <div style={sectionBarStyle}>Work Load Monitor Info</div>
        <div style={infoBoxStyle}>
          {monitorInfoItems ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px 24px',
              }}
            >
              {monitorInfoItems.map((line, index) => (
                <div
                  key={index}
                  style={{ fontWeight: 'bold', color: '#212223', fontSize: '14px' }}
                >
                  {line}
                </div>
              ))}
            </div>
          ) : (
            <Text>
              {isFetching
                ? 'Loading work load monitor info...'
                : `There were no files run in the past ${timeInterval} hours`}
            </Text>
          )}
        </div>

        <div style={sectionBarStyle}>Active File Summary</div>
        {FILE_SUMMARY_BUCKETS.filter(({ key }) => fileSummary?.[key]?.length).map(
          ({ key, title }) => (
            <SummaryTable
              key={key}
              title={title}
              columns={
                LINKABLE_FILE_SUMMARY_BUCKETS.has(key) ? fColsLinked : fColsPlain
              }
              data={fileSummary?.[key]}
            />
          )
        )}
        <div style={sectionBarStyle}>Active Transaction Set Summary</div>
        {TRANS_SUMMARY_BUCKETS.filter(
          ({ key }) => transSummary?.[key]?.length
        ).map(({ key, title }) => (
          <SummaryTable
            key={key}
            title={title}
            columns={tCols}
            data={transSummary?.[key]}
          />
        ))}
        {fileCompletedRows.length ? (
          <SummaryTable
            title="File Completed Summary"
            columns={fileCompletedColumns}
            data={fileCompletedRows}
          />
        ) : null}
      </div>

      <WlmTransactionDetailModal
        transSetId={detailParams?.transSetId}
        bucket={detailParams?.bucket}
        dataSource="CDB"
        isOpen={!!detailParams}
        onClose={() => setDetailParams(null)}
      />
    </Card>
  );
};
